import React from 'react';
import {
  Card,
  Tabs,
  Table,
  Space,
  DatePicker,
  Select,
  Typography,
  Statistic,
  Row,
  Col,
  Tag,
  Alert,
  Progress,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { reportsAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

type Range = { start: Dayjs; end: Dayjs };

type OrderRow = {
  id: number;
  employeeId: number;
  employeeName: string;
  username: string;
  deliveryDate: string;
  amount: number;
  orderId: number | null;
  orderStatus: string | null;
  supplierName: string;
  items: Array<{
    type: 'dish' | 'combo';
    name: string;
    category: string | null;
    quantity: number;
    unitPrice: number;
    amount: number;
  }>;
};

type LimitRow = {
  employeeId: number;
  employeeName: string;
  username: string;
  hasLimits: boolean;
  limitSource: string | null;
  usedMaxInPeriod?: boolean;
  dailyLimit: number | null;
  monthlyLimit: number | null;
  totalSpent: number;
  orderCount: number;
  orderDays: number;
  avgDailySpent: number;
  monthCount: number;
  dailyUsagePct: number | null;
  dailyPeriodUsagePct: number | null;
  monthlyUsagePct: number | null;
};

const money = (n: number | null | undefined) =>
  n == null ? '—' : `${n.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₽`;

const pctColor = (p: number | null) => {
  if (p == null) return undefined;
  if (p >= 90) return '#cf1322';
  if (p >= 70) return '#d48806';
  return '#389e0d';
};

const Reports: React.FC = () => {
  const { user } = useAuthStore();
  const canAccess = user?.role === 'customer' || user?.role === 'customer_manager' || user?.role === 'admin';

  const [preset, setPreset] = React.useState('this_month');
  const [range, setRange] = React.useState<Range>({
    start: dayjs().startOf('month'),
    end: dayjs().endOf('month'),
  });
  const [employeeFilter, setEmployeeFilter] = React.useState<number | 'all'>('all');
  const [employees, setEmployees] = React.useState<Array<{ id: number; fullName: string; username: string }>>([]);
  const [loadingOrders, setLoadingOrders] = React.useState(false);
  const [loadingLimits, setLoadingLimits] = React.useState(false);
  const [loadingTrace, setLoadingTrace] = React.useState(false);
  const [ordersData, setOrdersData] = React.useState<any>(null);
  const [limitsData, setLimitsData] = React.useState<any>(null);
  const [traceData, setTraceData] = React.useState<any>(null);
  const [traceDate, setTraceDate] = React.useState<Dayjs>(dayjs());
  const [activeTab, setActiveTab] = React.useState('orders');

  React.useEffect(() => {
    if (!canAccess || user?.role === 'admin') return;
    reportsAPI
      .employees()
      .then((data) => setEmployees(data.employees || []))
      .catch(() => setEmployees([]));
  }, [canAccess, user?.role]);

  const applyPreset = (key: string) => {
    setPreset(key);
    const now = dayjs();
    let next: Range;
    switch (key) {
      case 'today':
        next = { start: now.startOf('day'), end: now.endOf('day') };
        break;
      case 'this_week': {
        const day = now.day(); // 0=Sun
        const mon = now.subtract(day === 0 ? 6 : day - 1, 'day');
        next = { start: mon.startOf('day'), end: now.endOf('day') };
        break;
      }
      case 'last_week': {
        const day = now.day();
        const thisMon = now.subtract(day === 0 ? 6 : day - 1, 'day');
        const lastMon = thisMon.subtract(7, 'day');
        next = { start: lastMon.startOf('day'), end: lastMon.add(6, 'day').endOf('day') };
        break;
      }
      case 'this_month':
        next = { start: now.startOf('month'), end: now.endOf('month') };
        break;
      case 'last_month':
        next = {
          start: now.subtract(1, 'month').startOf('month'),
          end: now.subtract(1, 'month').endOf('month'),
        };
        break;
      default:
        return;
    }
    setRange(next);
  };

  const loadOrders = React.useCallback(async () => {
    if (!canAccess || user?.role === 'admin') return;
    setLoadingOrders(true);
    try {
      const data = await reportsAPI.employeeOrders({
        start: range.start.toISOString(),
        end: range.end.toISOString(),
        ...(employeeFilter !== 'all' ? { employeeId: employeeFilter } : {}),
      });
      setOrdersData(data);
    } catch (error: any) {
      message.error(error.response?.data?.error || error.message || 'Не удалось загрузить отчёт');
      setOrdersData(null);
    } finally {
      setLoadingOrders(false);
    }
  }, [canAccess, range, employeeFilter, user?.role]);

  const loadLimits = React.useCallback(async () => {
    if (!canAccess || user?.role === 'admin') return;
    setLoadingLimits(true);
    try {
      const data = await reportsAPI.limitUsage({
        start: range.start.toISOString(),
        end: range.end.toISOString(),
      });
      setLimitsData(data);
    } catch (error: any) {
      message.error(error.response?.data?.error || error.message || 'Не удалось загрузить отчёт');
      setLimitsData(null);
    } finally {
      setLoadingLimits(false);
    }
  }, [canAccess, range, user?.role]);

  const loadTrace = React.useCallback(async () => {
    if (!canAccess || user?.role === 'admin') return;
    setLoadingTrace(true);
    try {
      const data = await reportsAPI.orderTrace({
        date: traceDate.format('YYYY-MM-DD'),
        ...(employeeFilter !== 'all' ? { employeeId: employeeFilter } : {}),
      });
      setTraceData(data);
    } catch (error: any) {
      message.error(error.response?.data?.error || error.message || 'Не удалось загрузить проверку');
      setTraceData(null);
    } finally {
      setLoadingTrace(false);
    }
  }, [canAccess, traceDate, employeeFilter, user?.role]);

  React.useEffect(() => {
    if (activeTab === 'orders') loadOrders();
    else if (activeTab === 'limits') loadLimits();
    else if (activeTab === 'trace') loadTrace();
  }, [activeTab, loadOrders, loadLimits, loadTrace]);

  if (!canAccess) {
    return (
      <Card>
        <Alert type="info" showIcon message="Отчёты доступны заказчику и контролёру компании." />
      </Card>
    );
  }

  if (user?.role === 'admin') {
    return (
      <Card>
        <Alert
          type="info"
          showIcon
          message="Отчёты по заказам и лимитам доступны в кабинете заказчика (роль «Заказчик»)."
        />
      </Card>
    );
  }

  const periodControls = (
    <Space wrap style={{ marginBottom: 16 }}>
      {activeTab !== 'trace' && (
        <>
          <Select
            value={preset}
            style={{ width: 180 }}
            onChange={applyPreset}
            options={[
              { value: 'today', label: 'Сегодня' },
              { value: 'this_week', label: 'Эта неделя' },
              { value: 'last_week', label: 'Прошлая неделя' },
              { value: 'this_month', label: 'Этот месяц' },
              { value: 'last_month', label: 'Прошлый месяц' },
              { value: 'custom', label: 'Свой период' },
            ]}
          />
          <RangePicker
            value={[range.start, range.end]}
            allowClear={false}
            onChange={(vals) => {
              if (!vals?.[0] || !vals?.[1]) return;
              setPreset('custom');
              setRange({ start: vals[0].startOf('day'), end: vals[1].endOf('day') });
            }}
            format="DD.MM.YYYY"
          />
        </>
      )}
      {activeTab === 'trace' && (
        <DatePicker
          value={traceDate}
          allowClear={false}
          format="DD.MM.YYYY"
          onChange={(v) => v && setTraceDate(v)}
        />
      )}
      {(activeTab === 'orders' || activeTab === 'trace') && (
        <Select
          value={employeeFilter}
          style={{ minWidth: 260 }}
          showSearch
          optionFilterProp="label"
          placeholder="Сотрудник"
          onChange={(v: number | 'all') => setEmployeeFilter(v)}
          options={[
            { value: 'all', label: 'Все сотрудники' },
            ...employees.map((e) => ({
              value: e.id,
              label: `${e.fullName} (${e.username})`,
            })),
          ]}
        />
      )}
    </Space>
  );

  const orderColumns: ColumnsType<OrderRow> = [
    {
      title: 'Сотрудник',
      key: 'employee',
      render: (_, row) => (
        <div>
          <div>{row.employeeName}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {row.username}
          </Text>
        </div>
      ),
    },
    {
      title: 'Дата меню',
      dataIndex: 'deliveryDate',
      width: 120,
      render: (d: string) => dayjs(d).format('DD.MM.YYYY'),
      sorter: (a, b) => dayjs(a.deliveryDate).valueOf() - dayjs(b.deliveryDate).valueOf(),
      defaultSortOrder: 'ascend',
    },
    {
      title: 'Поставщик',
      dataIndex: 'supplierName',
      ellipsis: true,
    },
    {
      title: 'Заказ',
      dataIndex: 'orderId',
      width: 90,
      render: (id: number | null) => (id != null ? `#${id}` : '—'),
    },
    {
      title: 'Сумма',
      dataIndex: 'amount',
      width: 120,
      align: 'right',
      render: (v: number) => money(v),
      sorter: (a, b) => a.amount - b.amount,
    },
  ];

  const limitColumns: ColumnsType<LimitRow> = [
    {
      title: 'Сотрудник',
      key: 'employee',
      render: (_, row) => (
        <div>
          <div>{row.employeeName}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {row.username}
          </Text>
        </div>
      ),
    },
    {
      title: 'Потрачено',
      dataIndex: 'totalSpent',
      width: 120,
      align: 'right',
      render: (v: number) => money(v),
      sorter: (a, b) => a.totalSpent - b.totalSpent,
      defaultSortOrder: 'descend',
    },
    {
      title: 'Дней с заказами',
      dataIndex: 'orderDays',
      width: 110,
      align: 'center',
    },
    {
      title: 'Дневной лимит (за период)',
      dataIndex: 'dailyLimit',
      width: 150,
      align: 'right',
      render: (v: number | null, row: LimitRow) => (
        <span>
          {money(v)}
          {row.usedMaxInPeriod ? (
            <Tag style={{ marginLeft: 6 }} color="orange">
              макс
            </Tag>
          ) : null}
        </span>
      ),
    },
    {
      title: '% от дневного (ср.)',
      dataIndex: 'dailyUsagePct',
      width: 150,
      render: (v: number | null) =>
        v == null ? (
          '—'
        ) : (
          <span style={{ color: pctColor(v) }}>
            {v.toFixed(1)}%
          </span>
        ),
      sorter: (a, b) => (a.dailyUsagePct ?? -1) - (b.dailyUsagePct ?? -1),
    },
    {
      title: 'Месячный лимит (за период)',
      dataIndex: 'monthlyLimit',
      width: 170,
      align: 'right',
      render: (v: number | null, row: LimitRow) => {
        if (v == null) return '—';
        const base = row.monthCount > 1 ? `${money(v)} ×${row.monthCount}` : money(v);
        return (
          <span>
            {base}
            {row.usedMaxInPeriod ? (
              <Tag style={{ marginLeft: 6 }} color="orange">
                макс
              </Tag>
            ) : null}
          </span>
        );
      },
    },
    {
      title: '% от месячного',
      dataIndex: 'monthlyUsagePct',
      width: 160,
      render: (v: number | null) => {
        if (v == null) return '—';
        return (
          <Space direction="vertical" size={0} style={{ width: '100%' }}>
            <Progress
              percent={Math.min(v, 100)}
              size="small"
              status={v > 100 ? 'exception' : 'normal'}
              strokeColor={pctColor(v)}
              format={() => `${v.toFixed(1)}%`}
            />
          </Space>
        );
      },
      sorter: (a, b) => (a.monthlyUsagePct ?? -1) - (b.monthlyUsagePct ?? -1),
    },
    {
      title: 'Источник',
      dataIndex: 'limitSource',
      width: 100,
      render: (s: string | null, row) =>
        !row.hasLimits ? (
          <Tag>Нет лимита</Tag>
        ) : s === 'auto' ? (
          <Tag color="blue">Авто</Tag>
        ) : (
          <Tag>Вручную</Tag>
        ),
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div>
        <Title level={3} style={{ marginBottom: 4 }}>
          Отчёты
        </Title>
        <Text type="secondary">Анализ заказов сотрудников и использования лимитов за выбранный период</Text>
      </div>

      <Card>
        {periodControls}
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'orders',
              label: 'Заказы сотрудников',
              children: (
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <Alert
                    type="info"
                    showIcon
                    message="Кто, когда и на какую сумму заказывал (по дате меню). Выберите сотрудника или «Все сотрудники». Раскройте строку, чтобы увидеть блюда."
                  />
                  <Row gutter={16}>
                    <Col xs={12} sm={8}>
                      <Statistic title="Сумма" value={ordersData?.totalAmount ?? 0} precision={2} suffix="₽" loading={loadingOrders} />
                    </Col>
                    <Col xs={12} sm={8}>
                      <Statistic title="Записей" value={ordersData?.orderCount ?? 0} loading={loadingOrders} />
                    </Col>
                    <Col xs={12} sm={8}>
                      <Statistic title="Сотрудников" value={ordersData?.employeeCount ?? 0} loading={loadingOrders} />
                    </Col>
                  </Row>
                  <Table<OrderRow>
                    rowKey="id"
                    loading={loadingOrders}
                    dataSource={ordersData?.rows || []}
                    columns={orderColumns}
                    pagination={{ pageSize: 50, showSizeChanger: true }}
                    scroll={{ x: 800 }}
                    expandable={{
                      expandedRowRender: (row) =>
                        row.items?.length ? (
                          <Table
                            size="small"
                            pagination={false}
                            rowKey={(_, i) => `${row.id}-${i}`}
                            dataSource={row.items}
                            columns={[
                              {
                                title: 'Позиция',
                                dataIndex: 'name',
                                render: (name: string, item: OrderRow['items'][0]) => (
                                  <span>
                                    {name}{' '}
                                    {item.type === 'combo' && <Tag color="purple">Комбо</Tag>}
                                  </span>
                                ),
                              },
                              { title: 'Кол-во', dataIndex: 'quantity', width: 80 },
                              {
                                title: 'Цена',
                                dataIndex: 'unitPrice',
                                width: 100,
                                render: (v: number) => money(v),
                              },
                              {
                                title: 'Сумма',
                                dataIndex: 'amount',
                                width: 100,
                                render: (v: number) => money(v),
                              },
                            ]}
                          />
                        ) : (
                          <Text type="secondary">Детализация блюд недоступна (нет связи с пулом заказа)</Text>
                        ),
                    }}
                  />
                </Space>
              ),
            },
            {
              key: 'limits',
              label: 'Использование лимитов',
              children: (
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <Alert
                    type="info"
                    showIcon
                    message="Лимиты берутся за выбранный период (из истории изменений). Если за период лимит менялся — показывается большее значение (метка «макс»). % от месячного — сумма заказов относительно этого лимита × число месяцев в периоде."
                  />
                  <Row gutter={16}>
                    <Col xs={12} sm={8}>
                      <Statistic title="Потрачено всего" value={limitsData?.totalSpent ?? 0} precision={2} suffix="₽" loading={loadingLimits} />
                    </Col>
                    <Col xs={12} sm={8}>
                      <Statistic title="С лимитом" value={limitsData?.withLimitsCount ?? 0} loading={loadingLimits} />
                    </Col>
                    <Col xs={12} sm={8}>
                      <Statistic
                        title="Средний % месячного"
                        value={limitsData?.avgMonthlyUsagePct ?? 0}
                        precision={1}
                        suffix="%"
                        loading={loadingLimits}
                        valueStyle={{ color: pctColor(limitsData?.avgMonthlyUsagePct ?? null) }}
                      />
                    </Col>
                  </Row>
                  <Table<LimitRow>
                    rowKey="employeeId"
                    loading={loadingLimits}
                    dataSource={limitsData?.rows || []}
                    columns={limitColumns}
                    pagination={{ pageSize: 50, showSizeChanger: true }}
                    scroll={{ x: 1000 }}
                  />
                </Space>
              ),
            },
            {
              key: 'trace',
              label: 'Проверка заказа',
              children: (
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <Alert
                    type="info"
                    showIcon
                    message="Кто положил в корзину, кто нажал «Подтвердить», и дошёл ли заказ до поставщика"
                    description="До поставщика заказ доходит только после кнопки «Отправить заказ» у ответственного заказчика (появляется запись Order). Статус пула «sent» от крона сам по себе поставщику заказ не создаёт."
                  />
                  <Row gutter={16}>
                    <Col xs={12} sm={6}>
                      <Statistic title="В корзине" value={traceData?.summary?.employeesInCart ?? 0} loading={loadingTrace} />
                    </Col>
                    <Col xs={12} sm={6}>
                      <Statistic title="Подтвердили" value={traceData?.summary?.submitted ?? 0} loading={loadingTrace} />
                    </Col>
                    <Col xs={12} sm={6}>
                      <Statistic title="До поставщика" value={traceData?.summary?.reachedSupplier ?? 0} loading={loadingTrace} />
                    </Col>
                    <Col xs={12} sm={6}>
                      <Statistic title="Не дошло" value={traceData?.summary?.notReached ?? 0} loading={loadingTrace} valueStyle={{ color: '#cf1322' }} />
                    </Col>
                  </Row>
                  {(traceData?.pools || []).map((p: any) => (
                    <Alert
                      key={p.poolId}
                      type={p.reachedSupplier ? 'success' : p.status === 'cancelled' ? 'error' : 'warning'}
                      showIcon
                      message={`Пул #${p.poolId}: ${p.status}${p.reachedSupplier ? ' · заказ у поставщика' : ' · заказа у поставщика нет'}`}
                      description={`Позиций: ${p.itemsCount}, сумма: ${money(p.totalAmount)}${
                        p.orders?.length
                          ? `, orders: ${p.orders.map((o: any) => `#${o.id} (${o.status})`).join(', ')}`
                          : ''
                      }`}
                    />
                  ))}
                  <Table
                    rowKey="employeeId"
                    loading={loadingTrace}
                    dataSource={traceData?.employees || []}
                    pagination={{ pageSize: 50 }}
                    expandable={{
                      expandedRowRender: (row: any) =>
                        row.items?.length ? (
                          <Table
                            size="small"
                            pagination={false}
                            rowKey={(_: any, i?: number) => String(i)}
                            dataSource={row.items}
                            columns={[
                              { title: 'Позиция', dataIndex: 'name' },
                              { title: 'Кол-во', dataIndex: 'quantity', width: 80 },
                              {
                                title: 'Сумма',
                                dataIndex: 'amount',
                                width: 100,
                                render: (v: number) => money(v),
                              },
                            ]}
                          />
                        ) : null,
                    }}
                    columns={[
                      {
                        title: 'Сотрудник',
                        key: 'employee',
                        render: (_: any, row: any) => (
                          <div>
                            <div>{row.employeeName}</div>
                            <Text type="secondary">{row.username}</Text>
                          </div>
                        ),
                      },
                      {
                        title: 'В корзине',
                        dataIndex: 'addedToCart',
                        width: 100,
                        render: (v: boolean) => (v ? <Tag color="blue">да</Tag> : <Tag>нет</Tag>),
                      },
                      {
                        title: 'Подтвердил',
                        dataIndex: 'submittedToCustomer',
                        width: 120,
                        render: (v: boolean, row: any) =>
                          v ? (
                            <Tag color="orange">
                              да
                              {row.submittedAt
                                ? ` ${dayjs(row.submittedAt).format('HH:mm')}`
                                : ''}
                            </Tag>
                          ) : (
                            <Tag>нет</Tag>
                          ),
                      },
                      {
                        title: 'До поставщика',
                        dataIndex: 'reachedSupplier',
                        width: 130,
                        render: (v: boolean) =>
                          v ? <Tag color="green">да</Tag> : <Tag color="red">нет</Tag>,
                      },
                      {
                        title: 'Сумма',
                        dataIndex: 'amount',
                        width: 110,
                        render: (v: number) => money(v),
                      },
                      {
                        title: 'Вердикт',
                        dataIndex: 'verdict',
                        render: (v: string) => <Text>{v}</Text>,
                      },
                    ]}
                  />
                </Space>
              ),
            },
          ]}
        />
      </Card>
    </Space>
  );
};

export default Reports;
